// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'tdo_admin_token';

/**
 * AdminAuthCallback — handles redirect back from auth.emergentagent.com for
 * the admin panel. Reads `#session_id` from the URL fragment, exchanges it
 * with /api/admin/auth/google/session for an admin JWT, then routes to the
 * dashboard. Uses a useRef flag to survive StrictMode double-invocation.
 *
 * Hard rules enforced by the backend:
 *  - The Google e-mail must already exist in admin_users (no provisioning).
 *  - The admin must be ativo=True.
 *  - The token issued is an admin_access JWT (different namespace from user).
 */
const AdminAuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');
  const { refreshMe } = useAdminAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchange = async () => {
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : '';
      if (!sessionId) {
        setError('Não recebemos um identificador de sessão. Tente novamente.');
        setTimeout(() => navigate('/admin/login', { replace: true }), 2000);
        return;
      }
      try {
        const resp = await axios.post(
          `${API}/api/admin/auth/google/session`,
          {},
          { headers: { 'X-Session-ID': sessionId }, timeout: 15000 },
        );
        if (resp.data?.token) {
          localStorage.setItem(STORAGE_KEY, resp.data.token);
        }
        // Clear the fragment so back/forward doesn't replay.
        window.history.replaceState(null, '', window.location.pathname);
        // Refresh AdminAuthContext so the new token is reflected immediately
        // (otherwise the protected route would bounce back to /admin/login).
        try { await refreshMe?.(resp.data.token); } catch { /* noop */ }
        const target = resp.data?.admin?.must_change_password
          ? '/admin/change-password'
          : '/admin/dashboard';
        navigate(target, { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        // Map common backend rejections to human-friendly messages.
        let msg = detail;
        if (!msg) {
          if (status === 403) msg = 'Esta conta Google não está autorizada como admin.';
          else if (status === 401) msg = 'Sessão Google inválida ou expirada.';
          else if (status === 429) msg = 'Muitas tentativas. Aguarde alguns instantes.';
          else if (status === 503) msg = 'Serviço Google indisponível. Tente novamente.';
          else msg = 'Não foi possível concluir o login com Google.';
        }
        setError(typeof msg === 'string' ? msg : 'Falha ao concluir login.');
        setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
      }
    };
    exchange();
  }, [navigate, refreshMe]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4"
      data-testid="admin-auth-callback"
    >
      <div className="text-center max-w-sm">
        {error ? (
          <>
            <AlertCircle className="w-10 h-10 text-[#C62828] mx-auto mb-3" />
            <p
              className="text-sm text-[#C62828] font-medium"
              data-testid="admin-auth-callback-error"
            >
              {error}
            </p>
            <p className="text-xs text-[#94A3B8] mt-2">
              Redirecionando para a tela de login do painel…
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#1E3A5F] mx-auto mb-3" />
            <p className="text-sm text-[#0F1C2E] font-medium">
              Concluindo login com Google…
            </p>
            <p className="text-xs text-[#94A3B8] mt-1">Validando permissões de admin.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAuthCallback;
